import { newRequestKey } from "./requestKey";
import { assignmentOutcome } from './assignmentErrors';
import siteConfig from "../../siteConfig";
import { routingErrorPolicy, routingErrorCode } from '../../features/switchover/models/routingErrors';
import { assertSessionCurrent, sessionTicket } from '../../utils/sessionLifecycle';

const flights = new Map();
const storageKey = (actor, conversation) => `uzdpart360:assignment:${actor}:${conversation}`;
const validKey = key => typeof key === 'string' && /^[a-zA-Z0-9._:-]{1,191}$/.test(key);
const predecessors = saved => saved?.supersedesKeys ?? (saved?.supersedesKey ? [saved.supersedesKey] : []);
const same = (a, b) => a.assigned_to_user_id === b.assigned_to_user_id && a.reason === b.reason && a.expected_version === b.expected_version;
const validBody = (body, operation) => operation === 'release'
  ? body && Object.keys(body).length === 1 && Number.isSafeInteger(body.expected_version) && body.expected_version >= 0
  : body && Number.isInteger(body.assigned_to_user_id) && body.assigned_to_user_id > 0;

// Persist before dispatch. Retry replays the frozen request; a different target
// retires the frozen unresolved chain atomically through the server header.
export function runAssignment(actor, conversation, body, dispatch, operation = 'assign') {
  const ticket = sessionTicket();
  if (!Number.isInteger(Number(actor)) || Number(actor) <= 0) return Promise.reject(Object.assign(new Error("Sign in again before assigning a chat."), { assignmentState: "rejected", retryAction: "never" }));
  if (!['assign', 'release'].includes(operation) || !validBody(body, operation)) return Promise.reject(Object.assign(new Error(operation === 'release' ? 'Reload the chat to obtain its assignment version.' : "Choose a person to reassign this chat. Unassign is unavailable."), { assignmentState: "rejected", retryAction: "reload-chat" }));
  const scope = storageKey(actor, conversation);
  let saved;
  try {
    const raw = localStorage.getItem(scope);
    try { saved = raw === null ? null : JSON.parse(raw); } catch { throw new Error('invalid'); }
    if (saved && (saved.schema !== 1 || String(saved.actor) !== String(actor) || saved.conversation !== conversation
      || (saved.mayHaveCommitted !== undefined && typeof saved.mayHaveCommitted !== 'boolean')
      || !validKey(saved.key) || !Array.isArray(predecessors(saved)) || predecessors(saved).length > 10
      || new Set(predecessors(saved)).size !== predecessors(saved).length || predecessors(saved).some(key => !validKey(key) || key === saved.key)
      || (saved.supersedesKey != null && (!validKey(saved.supersedesKey) || (saved.supersedesKeys != null && (saved.supersedesKeys.length !== 1 || saved.supersedesKeys[0] !== saved.supersedesKey)))) || !['assign', 'release'].includes(saved.operation || 'assign') || !validBody(saved.body, saved.operation || 'assign')
      || (saved.body.reason !== undefined && typeof saved.body.reason !== "string")
      || !["pending", "uncertain", "completed", "rejected", "reload_required"].includes(saved.state))) throw new Error("invalid");
    if (raw !== null && !saved) throw new Error("invalid");
    if (saved?.state === 'rejected' && saved.retry_action === 'never' && same(saved.body, body)) throw Object.assign(new Error('WATI refused the takeover. Ask a supervisor.'), { assignmentState: 'rejected', retryAction: 'never' });
    if (saved?.state === "reload_required" && (!Number.isSafeInteger(saved.build_no) || saved.build_no >= siteConfig.build_no)) throw Object.assign(new Error("A new version is required. Reload before assigning."), { assignmentState: "reload_required", retryAction: "reload-page" });
    // A release retry is the same logical action even if a refresh now exposes
    // a newer assignment version. Replay the frozen version, never replace it.
    if (saved?.operation === 'release' && operation === 'release' && ['pending', 'uncertain'].includes(saved.state)) body = saved.body;
    const identical = saved && (saved.operation || 'assign') === operation && same(saved.body, body);
    const flight = flights.get(scope);
    if (flight) {
      if (identical) return flight;
      return flight.catch(() => {}).then(() => {
        assertSessionCurrent(ticket);
        return runAssignment(actor, conversation, body, dispatch, operation);
      });
    }
    const createsNew = !saved || !identical || ["completed", "reload_required"].includes(saved.state);
    // A definite replay rejection does not settle an earlier lost response
    // using this same key. Keep that uncertainty separately from its UI state.
    const unresolvedOwnKey = saved && (["pending", "uncertain"].includes(saved.state) || saved.mayHaveCommitted === true);
    const supersedesKeys = [...predecessors(saved)];
    if (saved && createsNew && unresolvedOwnKey) supersedesKeys.push(saved.key);
    if (supersedesKeys.length > 10) throw Object.assign(new Error("Too many unconfirmed assignments. Check this chat's current owner and reload before trying again."), { code: 'ASSIGNMENT_CHAIN_LIMIT', assignmentState: 'rejected', retryAction: 'reload-chat' });
    // After definite completion a later UI selection is a new deliberate
    // action (the owner may have changed elsewhere). Pending/uncertain retries
    // retain the original identity, including across reloads.
    if (createsNew) saved = { schema: 1, actor, conversation, operation, body: { ...body }, key: newRequestKey(), state: "pending", build_no: siteConfig.build_no, ...(supersedesKeys.length ? { supersedesKeys } : {}) };
    if (!createsNew && unresolvedOwnKey) saved.mayHaveCommitted = true;
    delete saved.supersedesKey;
    if (supersedesKeys.length) saved.supersedesKeys = supersedesKeys;
    saved.state = "pending";
    localStorage.setItem(scope, JSON.stringify(saved));
  } catch (error) {
    if (error.message !== "invalid") { error.assignmentState = error.assignmentState || "rejected"; return Promise.reject(error); }
    return Promise.reject(Object.assign(new Error("Saved assignment is invalid. Verify its outcome before assigning again."), { assignmentState: "rejected", retryAction: "never" }));
  }
  const attempt = saved;
  const request = Promise.resolve().then(() => { assertSessionCurrent(ticket); return dispatch(attempt.body, attempt.key, attempt.supersedesKeys?.length ? { 'Supersedes-Idempotency-Key': attempt.supersedesKeys.join(',') } : {}); }).then(result => {
    if (result?.success !== true) throw new Error("Assignment acceptance could not be confirmed.");
    // Do not mutate the frozen wire metadata before the completed receipt is
    // durable. If this write fails, catch must retain the exact header for replay.
    const completed = { ...attempt, state: "completed" };
    delete completed.mayHaveCommitted;
    delete completed.supersedesKey;
    delete completed.supersedesKeys;
    localStorage.setItem(scope, JSON.stringify(completed));
    return result;
  }).catch(error => {
    const code = routingErrorCode(error);
    const policy = routingErrorPolicy(code, error.details || error.response?.data?.error?.details);
    if (policy) {
      if (code === 'MESSAGING_OWNERSHIP_SEND_IN_FLIGHT') error.message = policy.message;
      error.code = code;
    }
    if (policy) attempt.retry_action = policy.retry;
    attempt.state = assignmentOutcome(error);
    if (attempt.state === "uncertain") attempt.mayHaveCommitted = true;
    error.assignmentKey = attempt.key;
    error.assignmentScope = scope;
    error.assignmentState = attempt.state;
    if (policy) error.retryAction = policy.retry;
    try { localStorage.setItem(scope, JSON.stringify(attempt)); } catch { /* The persisted key and predecessor remain available for safe recovery. */ }
    throw error;
  }).finally(() => flights.delete(scope));
  flights.set(scope, request);
  return request;
}
